import React from 'react';
import moment from 'moment';
import { 
  Edit, Event, Comment, PersonAdd, 
  PriorityHigh, LocationOn, Sync, 
  TrendingUp, Info, AccountCircle, 
  GroupAdd,
  GroupRemove
} from '@mui/icons-material';
import { useAppData } from '../../../../../context/AppDataContext';

const ActivityTimeline = ({ data }) => {
  const { userDD, getDDDataById, getMasterLabel } = useAppData();

  // Simple name resolver
  const getUserName = (id) => getDDDataById(userDD, id)?.label || `User (${id})`;

  /**
   * Helper to render JSON key-value pairs for Info Updates
   */
  const renderJsonFields = (jsonStr) => {
    try {
      const obj = JSON.parse(jsonStr);
      return Object.entries(obj).map(([key, value]) => {
        let displayVal = value;
        
        // Apply Master Data Labels inside the JSON if keys match
        if (key === 'calendarType') displayVal = getMasterLabel("calendar-type", Number(value));
        //if (key === 'status') displayVal = getMasterLabel("task-status", Number(value));
        //if (key === 'priority') displayVal = getMasterLabel("priority", Number(value));
        
        // Format key: "calendarType" -> "Calendar Type"
        const readableKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        
        return (
          <div key={key} className="d-flex justify-content-between border-bottom border-light py-1">
            <span className="text-muted" style={{ fontSize: '0.8rem' }}>{readableKey}:</span>
            <span className="fw-medium text-end">{displayVal}</span>
          </div>
        );
      });
    } catch (e) {
      return <div className="text-break">{jsonStr}</div>;
    }
  };

  return (
    <div className="container py-3" style={{height:"80vh", overflow:"scroll"}}>
      <div className="position-relative">
        {/* Timeline Track */}
        <div className="position-absolute border-start border-2 h-100" style={{ left: '16px', top: '0', borderColor: '#eee' }}></div>

        {data.map((item) => {
          // --- Icon & Theme Setup ---
          let icon = <Edit fontSize="small" />;
          let themeColor = '#6c757d';
          let bgColor = '#f8f9fa';

          if (item.activityType === 'info_update') { icon = <Info fontSize="small" />; themeColor = '#0d6efd'; bgColor = '#e7f1ff'; }
          else if (item.activityType === 'schedule_change') { icon = <Event fontSize="small" />; themeColor = '#fd7e14'; bgColor = '#fff5eb'; }
          else if (item.activityType === 'assignment_change') { icon = <PersonAdd fontSize="small" />; themeColor = '#0dcaf0'; bgColor = '#e0f7fa'; }
          else if (item.activityType === 'priority_change') { icon = <PriorityHigh fontSize="small" />; themeColor = '#dc3545'; bgColor = '#f8d7da'; }
          else if (item.activityType === 'status_change') { icon = <Sync fontSize="small" />; themeColor = '#6610f2'; bgColor = '#f3e5f5'; }
          else if (item.activityType === 'location_punch') { icon = <LocationOn fontSize="small" />; themeColor = '#198754'; bgColor = '#d1e7dd'; }
          else if (item.activityType === 'progress_update') { icon = <TrendingUp fontSize="small" />; themeColor = '#198754'; bgColor = '#d1e7dd'; }
          else if (item.activityType === 'contributor_add') { icon = <GroupAdd fontSize="small" />; themeColor = '#198754'; bgColor = '#d1e7dd'; }
          else if (item.activityType === 'contributor_remove') { icon = <GroupRemove fontSize="small" />; themeColor = '#dc3545'; bgColor = '#f8d7da';}

          return (
            <div key={item.id} className="mb-4 d-flex position-relative" style={{ zIndex: 1 }}>
              
              <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm border border-white"
                style={{ width: '34px', height: '34px', minWidth: '34px', backgroundColor: bgColor, color: themeColor }}>
                {icon}
              </div>

              <div className="ms-3 w-100">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-capitalize" style={{ fontSize: '0.9rem', color: themeColor }}>
                    {item.activityType.replace('_', ' ')}
                  </span>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>{moment(item.created_at).format('lll')}</small>
                </div>
                
                <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                  <AccountCircle sx={{ fontSize: 14, mr: 0.5 }} />
                  {getUserName(item.userId)}
                </div>

                <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid ${themeColor}`, borderRadius: '8px' }}>
                  <div className="card-body p-3">
                    <h6 className="small fw-bold border-bottom pb-2 mb-2">{item.title}</h6>
                    
                    <div className="rounded" style={{ backgroundColor: '#fcfcfc', fontSize: '0.85rem' }}>
                      
                      {/* 1. Schedule Change */}
                      {item.activityType === 'schedule_change' && (
                        <div>
                          <div className="text-muted mb-1">From: {item.oldValue ? item.oldValue.split(' to ').map(d => moment(d).format('lll')).join(' to ') : 'Initial'}</div>
                          <div className="fw-bold text-primary">To: {item.newValue ? item.newValue.split(' to ').map(d => moment(d).format('lll')).join(' to ') : 'N/A'}</div>
                        </div>
                      )}

                      {/* 2. Assignment Change */}
                      {item.activityType === 'assignment_change' && (
                        <div>
                          <div className="text-muted">Previous: {getUserName(item.oldValue)}</div>
                          <div className="fw-bold text-primary">Assigned: {getUserName(item.newValue)}</div>
                        </div>
                      )}

                      {/* 3. Info Update (Complex JSON objects) */}
                      {item.activityType === 'info_update' && (
                        <div className="row g-0">
                          {item.oldValue && (
                            <div className="col-12 mb-2 p-2 rounded bg-light border-start border-3 border-secondary-subtle">
                               <small className="text-muted fw-bold d-block mb-1 text-uppercase">Old values</small>
                               {renderJsonFields(item.oldValue)}
                            </div>
                          )}
                          <div className="col-12 p-2 rounded bg-primary-subtle border-start border-3 border-primary">
                             <small className="text-primary fw-bold d-block mb-1 text-uppercase">New updates</small>
                             {renderJsonFields(item.newValue)}
                          </div>
                        </div>
                      )}

                      {/* 4. Status/Priority Change */}
                      {(item.activityType === 'status_change' || item.activityType === 'priority_change') && (
                        <div>
                          <div className="text-muted">From: {getMasterLabel(item.activityType === 'status_change' ? "task-status" : "priority", Number(item.oldValue))}</div>
                          <div className="fw-bold text-primary">To: {getMasterLabel(item.activityType === 'status_change' ? "task-status" : "priority", Number(item.newValue))}</div>
                        </div>
                      )}

                      {/* 5. Location Punch */}
                      {item.activityType === 'location_punch' && item.newValue && (
                        <div>
                          <div className="fw-bold text-success mb-1">Check-in at Location</div>
                          <a href={JSON.parse(item.newValue).url} target="_blank" rel="noreferrer" className="btn btn-sm btn-success px-3 py-1">
                            <LocationOn sx={{ fontSize: 14, mr: 0.5 }} /> Open Map
                          </a>
                        </div>
                      )}

                      {/* 5. Add Contributor */}
                      {item.activityType === 'contributor_add' && item.newValue && (
                        <div>
                          <span className='text-success fw-bold'>"{getUserName(item.newValue)}"</span> is contributor now
                        </div>
                      )}

                      {/* 5. Remove Contributor */}
                      {item.activityType === 'contributor_remove' && item.newValue && (
                        <div>
                          <span className='text-danger fw-bold'>"{getUserName(item.newValue)}"</span> Removed from contributor list
                        </div>
                      )}

                      {/* Fallback for simple values (Progress, etc) */}
                      {!['schedule_change', 'assignment_change', 'info_update', 'status_change', 'priority_change', 'location_punch' ,"contributor_add", "contributor_remove"].includes(item.activityType) && (
                        <div>
                          {item.oldValue && <div className="text-muted">From: {item.oldValue}</div>}
                          {item.newValue && <div className="fw-bold text-primary">To: {item.newValue}</div>}
                        </div>
                      )}
                    </div>

                    {item.remark && (
                      <div className="mt-3 p-2 bg-light rounded small text-secondary">
                         <Comment sx={{ fontSize: 14, mr: 1, color: themeColor }} />
                         <em>{item.remark}</em>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityTimeline;